// Author a question into the bank — for every teacher.
//
// In CWY's own workspace that is the shared bank every school draws on; in a
// school it is that school's OWN bank, visible to its teachers and nobody
// else's. The server decides which from the caller's capability — this page
// only says where the question will land.
//
// The question itself is edited by the paper editor's own `PaperItemEditor`
// (type, marks, time, word limit, maths preview, TEAM with AI,
// video, concept tag), so a question authored here and one written into a
// paper cannot drift: they are one component. Around it: which subject, the
// other sub-topics it also tests, and difficulty. Saved questions come back in
// the paper editor's "Add from bank".
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { gradeLabel } from '@cwy/config/contracts';
import { apiGet, apiPost } from '@/lib/api';
import { useAccount } from '@/hooks/useSession';
import { isFirstParty } from '@/lib/tenant';
import type {
  Board,
  ConceptOption,
  ConceptTagResult,
  CustomPaperItem,
  Grade,
  TeacherClassroomDTO,
  TeamPart,
} from '@/types';
import { PaperItemEditor } from '@/components/teacher/paper/PaperItemEditor';
import {
  defaultItem,
  messageFor,
  serializeItems,
  type PaperQuestionType,
} from '@/components/teacher/paper/editorItems';

interface SubjectChoice {
  id: string;
  name: string;
  board: Board;
  grade: Grade;
}

const blank = (): CustomPaperItem => ({ ...defaultItem(1), marks: 2 });

const select =
  'rounded-lg border border-line bg-card px-2.5 py-1.5 text-sm outline-none focus:border-forest';

export function AuthorQuestionPage() {
  const account = useAccount(true);
  const bankName = isFirstParty
    ? 'CWY’s shared question bank'
    : `${account.data?.tenant?.name ?? 'your school'}’s question bank`;

  // Where the subject list comes from differs by workspace, because so does
  // what the server will accept: a school teacher authors in a subject they
  // teach here; CWY's content authors curate every cohort.
  const [board, setBoard] = useState<Board>('CBSE');
  const [grade, setGrade] = useState<Grade>('TEN');
  const catalogue = useQuery({
    queryKey: ['subjects', board, grade],
    enabled: isFirstParty,
    queryFn: () =>
      apiGet<{ id: string; name: string }[]>(`/subjects?board=${board}&grade=${grade}`),
  });
  const classrooms = useQuery({
    queryKey: ['teacher-classrooms'],
    enabled: !isFirstParty,
    queryFn: () => apiGet<TeacherClassroomDTO[]>('/teacher/classrooms'),
  });
  const subjects: SubjectChoice[] = useMemo(() => {
    if (isFirstParty) return (catalogue.data ?? []).map((s) => ({ ...s, board, grade }));
    const seen = new Map<string, SubjectChoice>();
    for (const room of classrooms.data ?? []) {
      for (const s of room.subjects) {
        if (!seen.has(s.id)) {
          seen.set(s.id, { id: s.id, name: s.name, board: room.board, grade: room.grade });
        }
      }
    }
    return [...seen.values()];
  }, [catalogue.data, classrooms.data, board, grade]);

  const [pickedSubject, setPickedSubject] = useState('');
  // The first subject until one is picked — derived, so no effect has to set it.
  const subject = subjects.find((s) => s.id === pickedSubject) ?? subjects[0] ?? null;

  const [item, setItem] = useState<CustomPaperItem>(blank);
  const [alsoTests, setAlsoTests] = useState<string[]>([]);
  const [difficulty, setDifficulty] = useState<'EASY' | 'MEDIUM' | 'HARD'>('MEDIUM');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const conceptsQuery = useQuery({
    queryKey: ['concepts', subject?.id],
    enabled: Boolean(subject),
    queryFn: () => apiGet<ConceptOption[]>(`/teacher/concepts?subjectId=${subject!.id}`),
  });
  const concepts = useMemo(() => conceptsQuery.data ?? [], [conceptsQuery.data]);

  /** A new subject is a new syllabus: the tags no longer apply. */
  const resetTags = () => {
    setItem((prev) => ({ ...prev, concept: '', subtopicId: null }));
    setAlsoTests([]);
  };

  const generate = useMutation({
    mutationFn: (vars: { parts: TeamPart[]; mode: 'fill' | 'rewrite' }) =>
      apiPost<CustomPaperItem>('/teacher/team-draft', {
        item: serializeItems([item])[0],
        subjectId: subject?.id,
        ...vars,
      }),
    onSuccess: (drafted) => {
      setItem(drafted);
      setMsg({ ok: true, text: 'AI wrote a draft. It stays marked until you edit it.' });
    },
    onError: (err) => setMsg({ ok: false, text: messageFor(err) }),
  });

  const tag = useMutation({
    mutationFn: () =>
      apiPost<ConceptTagResult>('/teacher/tag', { text: item.question, subjectId: subject?.id }),
    onSuccess: (t) => {
      if (!t) {
        setMsg({
          ok: false,
          text: 'Nothing in the question names a sub-topic in this subject — pick one with ✎ Change.',
        });
        return;
      }
      setItem((prev) => ({ ...prev, concept: t.name, subtopicId: t.subtopicId }));
      setAlsoTests((prev) => prev.filter((id) => id !== t.subtopicId));
      setMsg({
        ok: true,
        text: t.sure
          ? `Tagged ${t.name} · ${t.chapterName}.`
          : `Tagged ${t.name} · ${t.chapterName} on one shared word — a best guess, check it.`,
      });
    },
    onError: (err) => setMsg({ ok: false, text: messageFor(err) }),
  });

  const save = useMutation({
    mutationFn: () =>
      apiPost<{ id: string; visibility: 'GLOBAL' | 'TENANT' }>('/teacher/questions', {
        item: serializeItems([item])[0],
        subtopicId: item.subtopicId,
        board: subject!.board,
        grade: subject!.grade,
        difficulty,
        // Extra sub-topics are tags, not a second home: every report files a
        // question under ONE sub-topic, and two would count it twice.
        tags: alsoTests.map((id) => concepts.find((c) => c.subtopicId === id)?.name ?? id),
      }),
    onSuccess: () => {
      setMsg({
        ok: true,
        text: `Saved to ${bankName}. Pull it into any paper with “Add from bank”.`,
      });
      setItem({ ...blank(), type: item.type, marks: item.marks });
      setAlsoTests([]);
    },
    onError: (err) => setMsg({ ok: false, text: messageFor(err) }),
  });

  const isMcq = item.type === 'MCQ';
  const options = (item.options ?? []).filter((o) => o.trim());
  const canSave =
    Boolean(subject && item.subtopicId) &&
    item.question.trim().length >= 3 &&
    (isMcq
      ? options.length >= 2 &&
        item.correctOptionIdx !== null &&
        Boolean(item.options?.[item.correctOptionIdx!]?.trim())
      : item.answer.trim().length > 0);
  const busy = generate.isPending || tag.isPending || save.isPending;

  const byChapter = useMemo(() => {
    const groups = new Map<string, ConceptOption[]>();
    for (const c of concepts) groups.set(c.chapterName, [...(groups.get(c.chapterName) ?? []), c]);
    return [...groups];
  }, [concepts]);

  const loadingSubjects = isFirstParty ? catalogue.isLoading : classrooms.isLoading;

  return (
    <div className="mx-auto flex max-w-[860px] flex-col gap-4">
      <div>
        <h1 className="text-2xl font-bold text-forest">Author a question</h1>
        <p className="mt-1 text-sm text-ink-soft">
          Into {bankName}. It comes back in the paper editor under{' '}
          <Link to="/teacher/papers" className="font-semibold text-forest">
            Add from bank
          </Link>
          , with its TEAM already written.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-line bg-card p-3">
        {isFirstParty && (
          <>
            <select
              className={select}
              value={board}
              onChange={(e) => {
                setBoard(e.target.value as Board);
                setPickedSubject('');
                resetTags();
              }}
            >
              <option value="CBSE">CBSE</option>
              <option value="ICSE">ICSE</option>
            </select>
            <select
              className={select}
              value={grade}
              onChange={(e) => {
                setGrade(e.target.value as Grade);
                setPickedSubject('');
                resetTags();
              }}
            >
              <option value="TEN">Grade 10</option>
              <option value="TWELVE">Grade 12</option>
            </select>
          </>
        )}
        <select
          className={select}
          value={subject?.id ?? ''}
          disabled={subjects.length === 0}
          onChange={(e) => {
            setPickedSubject(e.target.value);
            resetTags();
          }}
        >
          {subjects.map((s) => (
            <option key={s.id} value={s.id}>
              {isFirstParty ? s.name : `${s.name} · ${s.board} ${gradeLabel(s.grade)}`}
            </option>
          ))}
        </select>
        <label className="ml-auto flex items-center gap-1.5 text-xs text-slate-500">
          Difficulty
          <select
            className={select}
            value={difficulty}
            onChange={(e) => setDifficulty(e.target.value as typeof difficulty)}
          >
            <option value="EASY">Easy</option>
            <option value="MEDIUM">Medium</option>
            <option value="HARD">Hard</option>
          </select>
        </label>
      </div>

      {!loadingSubjects && subjects.length === 0 ? (
        <p className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-800">
          {isFirstParty
            ? 'No subjects for this board and grade yet.'
            : 'You author in the subjects you teach here, and you have no classroom yet. Ask your school admin to give you one.'}
        </p>
      ) : (
        <>
          <PaperItemEditor
            idPrefix="author"
            item={item}
            concepts={concepts}
            editable
            busy={busy}
            generating={generate.isPending}
            tagging={tag.isPending}
            onPatch={(_, patch, part) => {
              setItem((prev) => ({
                ...prev,
                ...patch,
                aiDrafted: part ? (prev.aiDrafted ?? []).filter((p) => p !== part) : prev.aiDrafted,
              }));
              setMsg(null);
            }}
            onTypeChange={(_, type: PaperQuestionType) =>
              setItem((prev) => ({
                ...prev,
                type,
                options: type === 'MCQ' ? ['', '', '', ''] : null,
                correctOptionIdx: null,
              }))
            }
            onGenerate={(parts, mode) => generate.mutate({ parts, mode })}
            onTag={() => tag.mutate()}
          />

          {/* A board question often tests two ideas at once. The concept above
              is where every report files it; these say what else it asks. */}
          <div className="rounded-xl border border-line bg-card p-3">
            <p className="mb-2 text-fs-11 font-semibold uppercase tracking-wide text-slate-500">
              Also tests
            </p>
            <div className="flex flex-wrap items-center gap-1.5">
              {alsoTests.map((id) => {
                const c = concepts.find((x) => x.subtopicId === id);
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setAlsoTests((prev) => prev.filter((x) => x !== id))}
                    className="rounded-full border border-line bg-paper-sunk px-2.5 py-1 text-xs text-ink"
                    title="Remove"
                  >
                    {c?.name ?? id} <span className="text-slate-400">· {c?.chapterName}</span> ✕
                  </button>
                );
              })}
              <select
                className={`${select} text-xs`}
                value=""
                onChange={(e) =>
                  e.target.value && setAlsoTests((prev) => [...new Set([...prev, e.target.value])])
                }
              >
                <option value="">+ Add a sub-topic it also tests</option>
                {byChapter.map(([chapter, list]) => (
                  <optgroup key={chapter} label={chapter}>
                    {list
                      .filter(
                        (c) =>
                          c.subtopicId !== item.subtopicId && !alsoTests.includes(c.subtopicId),
                      )
                      .map((c) => (
                        <option key={c.subtopicId} value={c.subtopicId}>
                          {c.name}
                        </option>
                      ))}
                  </optgroup>
                ))}
              </select>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              disabled={!canSave || busy}
              onClick={() => save.mutate()}
              className="rounded-xl bg-forest px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
            >
              {save.isPending ? 'Saving…' : 'Save to the bank'}
            </button>
            {!item.subtopicId && (
              <span className="text-xs text-slate-500">Tag it with a sub-topic to save it.</span>
            )}
            {msg && (
              <span className={`text-sm ${msg.ok ? 'text-forest' : 'text-danger'}`}>
                {msg.text}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
